import React from 'react'
import { css, cx } from '@emotion/css'
import { readOnlyGridCss } from '../../legacy-design-system/text'
import { ExpandLink, CollapseLink } from '../../legacy-design-system/list'
import { maxApplicationsShownByDefault } from '../servers-grid/shared'
import { FormattedMessage } from 'react-intl'
import { ShowMoreApplicationsModal } from './show-more-applications-modal'
import { ellipsis } from 'polished'

const appListCss = css`
  display: flex;
  flex-direction: column;
  list-style-type: none;
  padding: 0;
  margin: 0;
`

const applicationListItemCss = css`
  ${ellipsis()}
`
const applicationListItemWithPaddingCss = cx(
  applicationListItemCss,
  css`
    padding-bottom: 5px;
  `
)

interface ApplicationsGridCellProps<
  TApplication extends {
    id: string
    name: string
  }
> {
  applications: TApplication[]
  applicationContext: { name: string; type: 'library' | 'server' }
  renderApplication: (application: TApplication) => React.ReactNode
  renderSkeletorApplication: (application: TApplication) => React.ReactNode
}

const ApplicationsLibraryExpansion = ({
  showToggle,
  isShowingAll,
  setIsShowingAll,
  total
}: {
  showToggle: boolean
  isShowingAll: boolean
  setIsShowingAll: React.Dispatch<React.SetStateAction<boolean>>
  total: number
}) => {
  if (showToggle) {
    return !isShowingAll ? (
      <ExpandLink onClick={() => setIsShowingAll(true)} total={total} />
    ) : (
      <CollapseLink onClick={() => setIsShowingAll(false)} />
    )
  }
  return null
}

export function ApplicationsGridCell<
  TApplication extends {
    id: string
    name: string
  }
>({
  applications,
  applicationContext,
  renderApplication,
  renderSkeletorApplication
}: ApplicationsGridCellProps<TApplication>) {
  const [isShowingAll, setIsShowingAll] = React.useState(false)
  const total = applications.length
  const showToggle = total > maxApplicationsShownByDefault
  let visibleItems = applications
  if (applicationContext.type !== 'library' || !isShowingAll) {
    visibleItems = applications.slice(0, maxApplicationsShownByDefault)
  }

  return visibleItems.length ? (
    <React.Fragment>
      <ul className={appListCss}>
        {visibleItems.map((item: TApplication, index) => {
          const requiresPadding =
            showToggle || (total > 1 && index !== total - 1)
          return (
            <li
              key={item.id}
              className={
                requiresPadding
                  ? applicationListItemWithPaddingCss
                  : applicationListItemCss
              }
            >
              {renderApplication(item)}
            </li>
          )
        })}
      </ul>
      {showToggle && applicationContext.type !== 'library' ? (
        <ExpandLink total={total} onClick={() => setIsShowingAll(true)} />
      ) : undefined}
      <ShowMoreApplicationsModal
        applicationsContext={applicationContext}
        isOpen={isShowingAll && applicationContext.type !== 'library'}
        closeDialog={() => setIsShowingAll(false)}
        applications={applications}
        renderApplication={renderSkeletorApplication}
      />
      {applicationContext.type === 'library' && (
        <ApplicationsLibraryExpansion
          showToggle={showToggle}
          isShowingAll={isShowingAll}
          setIsShowingAll={setIsShowingAll}
          total={total}
        />
      )}
    </React.Fragment>
  ) : (
    <FormattedMessage id="detail_timeToTestYourApps">
      {(text) => {
        return <span className={readOnlyGridCss}>{text}</span>
      }}
    </FormattedMessage>
  )
}
